import { MapPin } from "lucide-react";
import { CITIES } from "../data/content";
import { PageHero, Reveal, Eyebrow, useSeo } from "../components/Reveal";
import { AreaChecker } from "../components/AreaChecker";
import { UrgentButton } from "../components/UrgentModal";

const RING_CITIES = [
  { name: "Cedar Rapids", top: "46%", left: "42%", major: true },
  { name: "Iowa City", top: "58%", left: "47%", major: true },
  { name: "Marion", top: "41%", left: "47%" },
  { name: "Hiawatha", top: "38%", left: "40%" },
  { name: "Dubuque", top: "16%", left: "60%", major: true },
  { name: "Davenport", top: "62%", left: "74%", major: true },
  { name: "Muscatine", top: "70%", left: "60%" },
  { name: "Waterloo", top: "18%", left: "26%" },
  { name: "Coralville", top: "63%", left: "43%" },
  { name: "Tipton", top: "52%", left: "60%" },
  { name: "Maquoketa", top: "34%", left: "70%" },
  { name: "Vinton", top: "30%", left: "33%" },
];

export default function ServiceArea() {
  useSeo(
    "Service Area — Eastern Iowa Mobile Testing | SwiftTest Solutions",
    "SwiftTest provides mobile and on-site testing across Cedar Rapids, Iowa City, Marion, Hiawatha, Davenport & the Quad Cities, Dubuque, and a ~100-mile radius."
  );

  return (
    <div data-testid="service-area-page">
      <PageHero
        testId="service-area-hero"
        eyebrow="Service area"
        title="Eastern Iowa, covered end to end"
        lead="Based in the Cedar Rapids–Iowa City corridor, our mobile units serve roughly a 100-mile radius — from Dubuque to Davenport and everywhere in between."
      />

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            {/* RADIAL MAP */}
            <Reveal>
              <div
                data-testid="service-area-map"
                className="relative mx-auto aspect-square w-full max-w-lg"
              >
                {[100, 78, 56, 34].map((size) => (
                  <div
                    key={size}
                    className="absolute rounded-full border border-teal-300/50"
                    style={{
                      width: `${size}%`,
                      height: `${size}%`,
                      top: `${(100 - size) / 2}%`,
                      left: `${(100 - size) / 2}%`,
                    }}
                    aria-hidden="true"
                  />
                ))}
                <div className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-teal-700 text-center shadow-lg">
                  <span className="px-1 font-display text-[10px] font-bold leading-tight text-white">
                    We come to you
                  </span>
                </div>
                {RING_CITIES.map((c) => (
                  <span
                    key={c.name}
                    className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-full px-2.5 py-1 font-semibold shadow-sm ${
                      c.major
                        ? "bg-slate-900 text-[11px] text-white"
                        : "bg-white text-[10px] text-slate-700 ring-1 ring-slate-200"
                    }`}
                    style={{ top: c.top, left: c.left }}
                  >
                    <MapPin className={`h-3 w-3 ${c.major ? "text-teal-400" : "text-teal-600"}`} />
                    {c.name}
                  </span>
                ))}
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-widest text-teal-700">
                  ~100-mile radius
                </span>
              </div>
            </Reveal>

            {/* CHECKER + CITIES */}
            <div>
              <Reveal>
                <Eyebrow>Check your town</Eyebrow>
                <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                  Are we in your neighborhood?
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  If you can see your city below — or anywhere close — we can almost certainly reach
                  you, often the same day.
                </p>
              </Reveal>
              <Reveal delay={0.1} className="mt-8">
                <AreaChecker />
              </Reveal>
            </div>
          </div>

          <Reveal className="mt-20">
            <h3 className="font-display text-xl font-bold text-slate-900">
              Communities we regularly serve
            </h3>
            <div data-testid="city-chip-grid" className="mt-6 flex flex-wrap gap-2.5">
              {CITIES.map((c) => (
                <span
                  key={c}
                  data-testid={`city-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-teal-400 hover:text-teal-800"
                >
                  {c}
                </span>
              ))}
              <span className="rounded-full bg-teal-700 px-4 py-2 text-sm font-bold text-white">
                + surrounding areas
              </span>
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-500">
              Outside the circle? For multi-employee programs and urgent post-accident calls we
              frequently travel beyond our standard radius. Reach out and we'll confirm.
            </p>
            <div className="mt-8">
              <UrgentButton testId="service-area-need-testing-now-button" className="px-6 py-3.5 text-base" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
